// Deterministic models of protected queries for Lesson 2.11 ("Keep the Rules in the Queries").
// They simulate what PostgreSQL returns for the shared lesson dataset, what two transactions
// see under READ COMMITTED and how two paging methods move through Apollo's tasks; they run
// no SQL and send no requests.

export type Tone = 'neutral' | 'success' | 'warning';

const natural = (items: (string | number)[]) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${String(items.at(-1))}`;
export const idList = (tasks: readonly { id: number }[]) =>
  tasks.map((task) => task.id).join(', ') || 'none';
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

// --- shared lesson dataset -------------------------------------------------------------

export type Viewer = 'maya' | 'ravi' | 'jo';
export const viewers: Viewer[] = ['maya', 'ravi', 'jo'];
export const people: Record<Viewer, { id: number; name: string }> = {
  maya: { id: 1, name: 'Maya' },
  ravi: { id: 2, name: 'Ravi' },
  jo: { id: 3, name: 'Jo' },
};
const nameOf = (userId: number | null) =>
  Object.values(people).find((person) => person.id === userId)?.name ?? `user ${userId}`;

export interface LabTask {
  id: number;
  title: string;
  projectId: number | null;
  ownerId: number | null;
  completed: boolean;
}
/** Every row of the tasks table in the lesson dataset, ordered by ID. */
export const TASKS: readonly LabTask[] = [
  { id: 1, title: 'Prepare the demo', projectId: null, ownerId: 1, completed: false },
  { id: 4, title: "Read Maya's notes", projectId: null, ownerId: 1, completed: true },
  { id: 8, title: 'Check the examples', projectId: null, ownerId: 1, completed: false },
  { id: 9, title: 'Write the release note', projectId: 1, ownerId: null, completed: false },
  { id: 10, title: 'Check the dates', projectId: 1, ownerId: null, completed: false },
  { id: 11, title: 'Book the room', projectId: null, ownerId: 3, completed: false },
];
const PROJECT_NAMES: Record<number, string> = { 1: 'Apollo', 2: 'Support' };
export const MEMBERSHIPS: readonly {
  projectId: number;
  userId: number;
  role: 'manager' | 'member';
}[] = [
  { projectId: 1, userId: 1, role: 'manager' },
  { projectId: 1, userId: 2, role: 'member' },
  { projectId: 2, userId: 1, role: 'manager' },
];
export const projectsOf = (userId: number) =>
  MEMBERSHIPS.filter((membership) => membership.userId === userId).map(
    (membership) => membership.projectId,
  );
/** (project_id IS NULL AND owner_id = me) OR EXISTS (a membership of me in the task's project). */
export function isVisible(task: LabTask, userId: number) {
  return task.projectId === null
    ? task.ownerId === userId
    : projectsOf(userId).includes(task.projectId);
}
export function visibleBecause(task: LabTask, userId: number) {
  return task.projectId === null
    ? `${nameOf(task.ownerId)}'s personal task`
    : `${nameOf(userId)} is in ${PROJECT_NAMES[task.projectId] ?? 'the project'}`;
}
export const sqlValue = (value: number | null) => (value === null ? 'NULL' : String(value));

// --- query-filter: which rows leave the database ---------------------------------------

export type FilterPlace = 'python' | 'sql';
export const filterLabels: Record<FilterPlace, string> = {
  python: 'In Python after loading all',
  sql: 'In SQL',
};
/** The visibility condition every task query shares. */
export const FILTER_CONDITION =
  '(t.project_id IS NULL AND t.owner_id = %s) OR EXISTS (SELECT 1 FROM memberships m WHERE m.project_id = t.project_id AND m.user_id = %s)';

const pythonSet = (ids: number[]) => (ids.length ? `{${ids.join(', ')}}` : 'set()');
export function filterCode(viewer: Viewer, place: FilterPlace) {
  const { id, name } = people[viewer];
  if (place === 'python')
    return [
      'rows = connection.execute(',
      '    "SELECT t.id, t.title, t.project_id, t.owner_id FROM tasks AS t ORDER BY t.id"',
      ').fetchall()',
      `projects = ${pythonSet(projectsOf(id))}  # projects ${name} is a member of`,
      'tasks = [t for t in rows',
      `         if (t["project_id"] is None and t["owner_id"] == ${id})`,
      '         or t["project_id"] in projects]',
    ].join('\n');
  return [
    'tasks = connection.execute(',
    '    """SELECT t.id, t.title, t.project_id, t.owner_id FROM tasks AS t',
    '       WHERE (t.project_id IS NULL AND t.owner_id = %s)',
    '          OR EXISTS (SELECT 1 FROM memberships m',
    '                     WHERE m.project_id = t.project_id AND m.user_id = %s)',
    '       ORDER BY t.id""",',
    `    (${id}, ${id}),`,
    ').fetchall()',
  ].join('\n');
}

export interface FilterResult {
  code: string;
  /** Rows PostgreSQL sends over the connection. */
  sent: LabTask[];
  /** Rows the viewer's page shows. */
  shown: LabTask[];
  /** Rows a route that forgets the Python filter shows. */
  forgotten: LabTask[];
  /** Rows the viewer may not see that still reach a page. */
  leaked: LabTask[];
  onPage: (task: LabTask) => string;
  forgetLine: string;
  message: string;
  tone: Tone;
}
export function queryFilter(viewer: Viewer, place: FilterPlace): FilterResult {
  const { id, name } = people[viewer];
  const shown = TASKS.filter((task) => isVisible(task, id));
  const hidden = TASKS.filter((task) => !isVisible(task, id));
  const python = place === 'python';
  const sent = python ? [...TASKS] : shown;
  const forgotten = python ? [...TASKS] : shown;
  const leaked = python ? hidden : [];
  return {
    code: filterCode(viewer, place),
    sent,
    shown,
    forgotten,
    leaked,
    onPage: (task) =>
      isVisible(task, id) ? `yes: ${visibleBecause(task, id)}` : 'no: dropped in Python',
    forgetLine: python
      ? `Shows all ${TASKS.length}: ${idList(TASKS)}. Leaks ${idList(leaked)}`
      : `Shows ${idList(shown)}: the rule is in the shared query, so nothing leaks`,
    message: python
      ? `PostgreSQL sent all ${TASKS.length} tasks and Python kept ${shown.length} (${natural(
          shown.map((task) => task.id),
        )}). ${hidden.length === 1 ? 'Task' : 'Tasks'} ${natural(
          hidden.map((task) => task.id),
        )} crossed the connection anyway, and a route that forgets the filter shows ${
          hidden.length === 1 ? 'it' : 'them'
        }.`
      : `PostgreSQL sent only the ${plural(shown.length, 'task')} ${name} may see (${natural(
          shown.map((task) => task.id),
        )}). The rest never left the database, so no route that uses this query can show them.`,
    tone: python ? 'warning' : 'success',
  };
}

// --- id-guessing: can Jo read task 9 by typing its ID ----------------------------------

export type Lookup = 'id' | 'visible';
export const lookupLabels: Record<Lookup, string> = {
  id: 'By ID only',
  visible: 'By ID and visibility',
};
export const GUESS_IDS = [9, 10, 11] as const;
export type GuessId = (typeof GUESS_IDS)[number];
/** An ID no task has, for comparing a hidden task with a missing one. */
export const MISSING_ID = 99;
export const TASK_NOT_FOUND = '{"detail":"Task was not found"}';
const JO = people.jo.id;
export const taskJson = (task: LabTask) =>
  JSON.stringify({ id: task.id, title: task.title, completed: task.completed });

export function guessQuery(lookup: Lookup, id: number) {
  const lines = ['SELECT t.id, t.title, t.completed', 'FROM tasks AS t', 'WHERE t.id = %s'];
  if (lookup === 'id') return [...lines, `-- params: (${id},)`].join('\n');
  return [
    ...lines,
    '  AND ((t.project_id IS NULL AND t.owner_id = %s)',
    '       OR EXISTS (SELECT 1 FROM memberships m',
    '                  WHERE m.project_id = t.project_id AND m.user_id = %s))',
    `-- params: (${id}, ${JO}, ${JO})`,
  ].join('\n');
}
export function describeGuessTarget(id: number) {
  const task = TASKS.find((entry) => entry.id === id);
  if (!task) return 'No task has this ID';
  return task.projectId === null
    ? `${nameOf(task.ownerId)}'s personal task (owner_id = ${sqlValue(task.ownerId)})`
    : `${PROJECT_NAMES[task.projectId] ?? 'A project'}'s task; Jo is not a member`;
}

export interface GuessResult {
  lookup: Lookup;
  id: number;
  status: 200 | 404;
  query: string;
  request: string[];
  response: string[];
  /** What PostgreSQL returned to the route. */
  returned: string;
  /** Status and body on one line. */
  answer: string;
  message: string;
  tone: Tone;
}
export function guessTask(lookup: Lookup, id: number): GuessResult {
  const task = TASKS.find((entry) => entry.id === id);
  const found = task && (lookup === 'id' || isVisible(task, JO)) ? task : undefined;
  const body = found ? taskJson(found) : TASK_NOT_FOUND;
  const own = task?.projectId === null && task.ownerId === JO;
  let message: string;
  let tone: Tone;
  if (!task) {
    message = `404 Task was not found: no task has ID ${id}.`;
    tone = 'neutral';
  } else if (found && !own) {
    message = `200 OK: Jo read ${PROJECT_NAMES[task.projectId ?? 0] ?? 'a project'}'s task ${id} "${task.title}" although he is in no project. The query only asked whether the ID exists.`;
    tone = 'warning';
  } else if (found && lookup === 'id') {
    message = `200 OK: Jo read his own task ${id}. The answer is right here, but this query would return any task Jo names.`;
    tone = 'neutral';
  } else if (found) {
    message = `200 OK: task ${id} is Jo's personal task, so it passes the visibility condition.`;
    tone = 'success';
  } else {
    message = `404 Task was not found: the same answer as for task ${MISSING_ID}, which does not exist, so Jo cannot even tell that task ${id} exists.`;
    tone = 'success';
  }
  return {
    lookup,
    id,
    status: found ? 200 : 404,
    query: guessQuery(lookup, id),
    request: [`GET /tasks/${id} HTTP/1.1`, "cookie: __Host-fde_lab=… (Jo's session)"],
    response: [
      found ? 'HTTP/1.1 200 OK' : 'HTTP/1.1 404 Not Found',
      'content-type: application/json',
      '',
      body,
    ],
    returned: found
      ? `1 row: ${found.id}, '${found.title}', ${found.completed ? 'true' : 'false'}`
      : 'No row, so find_task raises MissingRecord("Task was not found")',
    answer: `${found ? 200 : 404} ${body}`,
    message,
    tone,
  };
}
export const guessHistoryLine = (result: GuessResult) =>
  `GET /tasks/${result.id} · ${lookupLabels[result.lookup]} → ${result.status}`;

// --- last-manager-race: can two managers both step down --------------------------------

export type RaceTx = 'A' | 'B';
export type RaceMember = 'maya' | 'ravi';
export type RaceRole = 'manager' | 'member';
export type RacePhase =
  'lock' | 'waiting' | 'demote' | 'count' | 'commit' | 'committed' | 'refused';
export interface RaceTxState {
  /** The next step this transaction runs, or where it stopped. */
  phase: RacePhase;
  /** Its own demotion, which only it sees until it commits. */
  demoted: boolean;
  waited: boolean;
  counted: number | null;
}
export interface RaceEntry {
  step: string;
  saw: string;
}
export interface RaceState {
  lock: boolean;
  holder: RaceTx | null;
  committed: Record<RaceMember, RaceRole>;
  tx: Record<RaceTx, RaceTxState>;
  timeline: RaceEntry[];
  message: string;
}
export type RaceAction = 'step-a' | 'step-b' | 'toggle-lock' | 'reset';

export const raceMembers: RaceMember[] = ['maya', 'ravi'];
/** A: Maya steps down. B: Ravi steps down. */
export const raceUser: Record<RaceTx, RaceMember> = { A: 'maya', B: 'ravi' };
export const LAST_MANAGER = '{"detail":"A project needs at least one manager."}';
export const stepDownBody = (tx: RaceTx) =>
  JSON.stringify({ project_id: 1, user_id: people[raceUser[tx]].id, role: 'member' });
const other = (tx: RaceTx): RaceTx => (tx === 'A' ? 'B' : 'A');
const nameOfMember = (member: RaceMember) => people[member].name;
export const RACE_START =
  'Apollo has two managers, Maya and Ravi. Each transaction demotes one of them, counts the managers left and commits only if at least one remains.';

const freshTx = (lock: boolean): RaceTxState => ({
  phase: lock ? 'lock' : 'demote',
  demoted: false,
  waited: false,
  counted: null,
});
export const initialRace = (lock = false): RaceState => ({
  lock,
  holder: null,
  committed: { maya: 'manager', ravi: 'manager' },
  tx: { A: freshTx(lock), B: freshTx(lock) },
  timeline: [],
  message: RACE_START,
});

export const raceEnded = (state: RaceState, tx: RaceTx) =>
  state.tx[tx].phase === 'committed' || state.tx[tx].phase === 'refused';
export const raceStarted = (state: RaceState, tx: RaceTx) =>
  state.tx[tx].phase !== (state.lock ? 'lock' : 'demote') || state.tx[tx].demoted;
/** Committed roles plus this transaction's own uncommitted change (READ COMMITTED). */
export function raceView(state: RaceState, tx: RaceTx): Record<RaceMember, RaceRole> {
  const view = { ...state.committed };
  if (state.tx[tx].demoted) view[raceUser[tx]] = 'member';
  return view;
}
export const managersIn = (roles: Record<RaceMember, RaceRole>) =>
  raceMembers.filter((member) => roles[member] === 'manager');
export const managerNames = (roles: Record<RaceMember, RaceRole>) =>
  managersIn(roles).map(nameOfMember).join(', ') || 'none';

export function raceStepLabel(state: RaceState, tx: RaceTx) {
  switch (state.tx[tx].phase) {
    case 'lock':
      return `${tx}: lock Apollo`;
    case 'waiting':
      return `${tx}: waiting for the lock`;
    case 'demote':
      return `${tx}: demote ${nameOfMember(raceUser[tx])}`;
    case 'count':
      return `${tx}: count managers`;
    case 'commit':
      return `${tx}: commit`;
    case 'committed':
      return `${tx}: committed`;
    case 'refused':
      return `${tx}: rolled back`;
  }
}
export const raceCanStep = (state: RaceState, tx: RaceTx) =>
  state.tx[tx].phase !== 'waiting' && !raceEnded(state, tx);

export function raceStatus(state: RaceState, tx: RaceTx) {
  const current = state.tx[tx];
  if (current.phase === 'committed') return `200 ${stepDownBody(tx)}`;
  if (current.phase === 'refused') return `409 ${LAST_MANAGER}`;
  if (current.phase === 'waiting') return `Waiting for ${other(tx)}'s lock`;
  if (!raceStarted(state, tx)) return 'Not started';
  return [
    'Open',
    ...(state.holder === tx ? ['holds the lock'] : []),
    ...(current.demoted ? [`${nameOfMember(raceUser[tx])} demoted, not committed`] : []),
  ].join(', ');
}

/** Release tx's lock; a transaction waiting for it gets it and runs its next statement. */
function release(state: RaceState, tx: RaceTx, outcome: 'committed' | 'rolled back'): RaceState {
  if (state.holder !== tx) return state;
  const waiting = other(tx);
  if (state.tx[waiting].phase !== 'waiting') return { ...state, holder: null };
  const sees =
    outcome === 'committed'
      ? `its next statement sees ${nameOfMember(raceUser[tx])}'s committed change`
      : `${tx}'s change was undone`;
  return {
    ...state,
    holder: waiting,
    tx: { ...state.tx, [waiting]: { ...state.tx[waiting], phase: 'demote' } },
    timeline: [
      ...state.timeline,
      { step: `${waiting}: lock granted`, saw: `${tx} ${outcome}, so ${waiting} gets the lock` },
    ],
    message: `${tx} ${outcome} and released the lock. ${waiting} gets it now, and ${sees}.`,
  };
}

function raceStep(state: RaceState, tx: RaceTx): RaceState {
  if (!raceCanStep(state, tx)) return state;
  const current = state.tx[tx];
  const member = raceUser[tx];
  const name = nameOfMember(member);
  const step = raceStepLabel(state, tx);
  const withTx = (next: Partial<RaceTxState>, entry: string, message: string): RaceState => ({
    ...state,
    tx: { ...state.tx, [tx]: { ...current, ...next } },
    timeline: [...state.timeline, { step, saw: entry }],
    message,
  });
  switch (current.phase) {
    case 'lock':
      if (state.holder && state.holder !== tx)
        return withTx(
          { phase: 'waiting', waited: true },
          `Waits: ${state.holder} holds the lock on Apollo`,
          `${tx} asks for the same lock and waits. It runs nothing more until ${state.holder} commits or rolls back.`,
        );
      return {
        ...withTx(
          { phase: 'demote' },
          "Got the lock on Apollo's project row",
          `${tx} holds the lock on Apollo's project row until it commits or rolls back.`,
        ),
        holder: tx,
      };
    case 'demote':
      return withTx(
        { phase: 'count', demoted: true },
        `${name} is a member inside ${tx} only`,
        `${tx} changed ${name} to member. Only ${tx} sees this until it commits.`,
      );
    case 'count': {
      const managers = managersIn(raceView(state, tx));
      const counted = managers.length;
      const rival = other(tx);
      if (counted === 0) {
        const refused = withTx(
          { phase: 'refused', demoted: false, counted },
          '0 managers: LastManager, ROLLBACK, 409',
          `${tx} counted 0 managers: ${nameOfMember(raceUser[rival])}'s demotion is committed and ${name}'s is ${tx}'s own. LastManager rolls ${tx} back and the API answers 409 ${LAST_MANAGER}`,
        );
        return release(refused, tx, 'rolled back');
      }
      const unseen = state.tx[rival].demoted;
      return withTx(
        { phase: 'commit', counted },
        `${plural(counted, 'manager')}: ${managers.map(nameOfMember).join(', ')}`,
        unseen
          ? `${tx} counted ${plural(counted, 'manager')}: ${managers.map(nameOfMember).join(', ')}. ${rival} has demoted ${nameOfMember(raceUser[rival])} too, but ${rival} has not committed, so ${tx} cannot see it.`
          : `${tx} counted ${plural(counted, 'manager')}: ${managers.map(nameOfMember).join(', ')}, so ${tx} may commit.`,
      );
    }
    case 'commit': {
      const committed: RaceState = {
        ...withTx(
          { phase: 'committed', demoted: false },
          `200: ${name}'s member role is committed`,
          `${tx} committed: ${name} is now a member of Apollo, and the API answers 200.`,
        ),
        committed: { ...state.committed, [member]: 'member' },
      };
      return release(committed, tx, 'committed');
    }
    default:
      return state;
  }
}

/** The closing message once both transactions have finished. */
function finish(state: RaceState): RaceState {
  if (!raceEnded(state, 'A') || !raceEnded(state, 'B')) return state;
  const refused = (['A', 'B'] as RaceTx[]).find((tx) => state.tx[tx].phase === 'refused');
  if (!refused)
    return {
      ...state,
      message:
        'Both transactions counted 1 manager and both committed, so Apollo has no manager. Each count ran before the other transaction committed, so each still saw the other manager.',
    };
  const first = other(refused);
  const reason = state.lock
    ? state.tx[refused].waited
      ? ` The lock made ${refused} wait until ${first} committed, so its count saw the truth.`
      : ` ${refused} started after ${first} had finished, so it never had to wait.`
    : ` Without the lock this only worked because ${first} committed before ${refused} counted.`;
  return {
    ...state,
    message: `${refused} counted 0 managers after ${first} committed and was refused: 409 ${LAST_MANAGER}. Apollo keeps ${managerNames(state.committed)} as manager.${reason}`,
  };
}

export function raceTransition(state: RaceState, action: RaceAction): RaceState {
  switch (action) {
    case 'reset':
      return initialRace();
    case 'toggle-lock':
      return initialRace(!state.lock);
    case 'step-a':
      return finish(raceStep(state, 'A'));
    case 'step-b':
      return finish(raceStep(state, 'B'));
  }
}
export function raceTone(state: RaceState): Tone {
  if (!raceEnded(state, 'A') || !raceEnded(state, 'B')) return 'neutral';
  if (!managersIn(state.committed).length) return 'warning';
  return state.lock ? 'success' : 'neutral';
}
export function raceCode(lock: boolean) {
  return [
    'BEGIN;',
    ...(lock ? ['SELECT id FROM projects WHERE id = 1 FOR UPDATE;'] : []),
    "UPDATE memberships SET role = 'member'",
    ' WHERE project_id = 1 AND user_id = %s;  -- A: 1 (Maya), B: 2 (Ravi)',
    'SELECT count(*) AS managers FROM memberships',
    " WHERE project_id = 1 AND role = 'manager';",
    '-- 0 managers: raise LastManager (ROLLBACK, 409)',
    'COMMIT;',
  ].join('\n');
}
/** Apollo's memberships: committed role and what each open transaction sees. */
export function raceRows(state: RaceState): string[][] {
  const viewOf = (tx: RaceTx, member: RaceMember) =>
    raceEnded(state, tx) ? 'ended' : raceView(state, tx)[member];
  return raceMembers.map((member) => [
    `${people[member].id} (${nameOfMember(member)})`,
    state.committed[member],
    viewOf('A', member),
    viewOf('B', member),
  ]);
}

// --- keyset-paging: does the next page skip or repeat a task ---------------------------

export type PagingMethod = 'offset' | 'after';
export type PagingOrder = 'oldest' | 'newest';
export type PagingEvent = 'none' | 'delete' | 'insert';
export type PagesLoaded = 0 | 1 | 2;
export const pagingMethodLabels: Record<PagingMethod, string> = {
  offset: 'OFFSET',
  after: 'after ID',
};
export const pagingOrderLabels: Record<PagingOrder, string> = {
  oldest: 'Oldest first (ORDER BY id)',
  newest: 'Newest first (ORDER BY id DESC)',
};
export const pagingEventLabels: Record<PagingEvent, string> = {
  none: 'None',
  delete: 'Task 9 deleted',
  insert: 'Task 15 added',
};
export const PAGE_SIZE = 2;
export interface PagingTask {
  id: number;
  title: string;
}
/** Apollo's tasks: 9 and 10 from the dataset, 12 to 14 added later (11 is Jo's personal task). */
export const LAUNCH_TASKS: readonly PagingTask[] = [
  { id: 9, title: 'Write the release note' },
  { id: 10, title: 'Check the dates' },
  { id: 12, title: 'Draft the agenda' },
  { id: 13, title: 'Send the invites' },
  { id: 14, title: 'Print the badges' },
];
/** IDs only grow, so the new task gets the highest ID. */
export const NEW_TASK: PagingTask = { id: 15, title: 'Confirm the venue' };
const DELETED_ID = 9;

export interface PagingPage {
  query: string;
  /** Rows PostgreSQL returned. */
  fetched: PagingTask[];
  /** Rows on the page. */
  shown: PagingTask[];
  nextAfter: number | null;
  next: string;
}
export interface PagingRun {
  page1: PagingPage | null;
  page2: PagingPage | null;
  /** Apollo's tasks as the table holds them now, with where each one appeared. */
  rows: [number, string, string][];
  skipped: number[];
  repeated: number[];
  between: string;
  message: string;
  tone: Tone;
}

const sortTasks = (tasks: PagingTask[], order: PagingOrder) =>
  [...tasks].sort((a, b) => (order === 'oldest' ? a.id - b.id : b.id - a.id));
const comesBefore = (a: PagingTask, b: PagingTask, order: PagingOrder) =>
  order === 'oldest' ? a.id < b.id : a.id > b.id;
const afterCondition = (order: PagingOrder, after: number) =>
  order === 'oldest' ? `id > ${after}` : `id < ${after}`;

export function pagingQuery(
  method: PagingMethod,
  order: PagingOrder,
  page: 1 | 2,
  after: number | null,
) {
  const direction = order === 'oldest' ? 'ORDER BY id' : 'ORDER BY id DESC';
  if (method === 'offset')
    return [
      'SELECT id, title FROM tasks',
      'WHERE project_id = 1',
      direction,
      `LIMIT ${PAGE_SIZE} OFFSET ${(page - 1) * PAGE_SIZE}`,
    ].join('\n');
  // The API's first page sends after=0; a newest-first list starts with no ID condition.
  const start = order === 'oldest' ? (after ?? 0) : after;
  return [
    'SELECT id, title FROM tasks',
    `WHERE project_id = 1${start === null ? '' : ` AND ${afterCondition(order, start)}`}`,
    direction,
    `LIMIT ${PAGE_SIZE + 1}  -- ${PAGE_SIZE} + 1 extra row`,
  ].join('\n');
}

function loadPage(
  tasks: PagingTask[],
  method: PagingMethod,
  order: PagingOrder,
  page: 1 | 2,
  after: number | null,
): PagingPage {
  const sorted = sortTasks(tasks, order);
  const query = pagingQuery(method, order, page, after);
  if (method === 'offset') {
    const offset = (page - 1) * PAGE_SIZE;
    const fetched = sorted.slice(offset, offset + PAGE_SIZE);
    return {
      query,
      fetched,
      shown: fetched,
      nextAfter: null,
      next: `Next page: OFFSET ${offset + PAGE_SIZE}`,
    };
  }
  const start = order === 'oldest' ? (after ?? 0) : after;
  const rest =
    start === null
      ? sorted
      : sorted.filter((task) => (order === 'oldest' ? task.id > start : task.id < start));
  const fetched = rest.slice(0, PAGE_SIZE + 1);
  const shown = fetched.slice(0, PAGE_SIZE);
  const nextAfter = fetched.length > PAGE_SIZE ? (shown.at(-1)?.id ?? null) : null;
  return {
    query,
    fetched,
    shown,
    nextAfter,
    next:
      nextAfter === null
        ? `next_after: null (${plural(fetched.length, 'row')} came back, so this is the last page)`
        : `next_after: ${nextAfter} (${fetched.length} rows came back, so another page exists)`,
  };
}

export const pagingReturned = (page: PagingPage, task: PagingTask) =>
  page.shown.some((entry) => entry.id === task.id) ? 'shown' : 'extra row: more exist';

export function pagingRun(
  method: PagingMethod,
  order: PagingOrder,
  event: PagingEvent,
  loaded: PagesLoaded,
): PagingRun {
  const before = [...LAUNCH_TASKS];
  const after =
    event === 'delete'
      ? before.filter((task) => task.id !== DELETED_ID)
      : event === 'insert'
        ? [...before, NEW_TASK]
        : before;
  const page1 = loaded >= 1 ? loadPage(before, method, order, 1, null) : null;
  const page2 =
    loaded === 2 && page1
      ? loadPage(after, method, order, 2, method === 'after' ? page1.nextAfter : null)
      : null;
  const on = (page: PagingPage | null, task: PagingTask) =>
    Boolean(page?.shown.some((entry) => entry.id === task.id));
  const current = loaded === 2 ? after : before;
  const last = page2?.shown.at(-1);
  const skipped =
    page2 && last
      ? current
          .filter(
            (task) =>
              task.id !== NEW_TASK.id &&
              !on(page1, task) &&
              !on(page2, task) &&
              comesBefore(task, last, order),
          )
          .map((task) => task.id)
      : [];
  const repeated = current.filter((task) => on(page1, task) && on(page2, task)).map((t) => t.id);
  const listed = sortTasks(
    loaded === 2 && event === 'delete'
      ? [...current, ...before.filter((task) => task.id === DELETED_ID)]
      : current,
    order,
  );
  const rows = listed.map((task): [number, string, string] => {
    const first = on(page1, task);
    const second = on(page2, task);
    let where = 'not shown yet';
    if (loaded === 2 && event === 'delete' && task.id === DELETED_ID)
      where = first ? 'page 1, then deleted' : 'deleted before page 2';
    else if (first && second) where = 'pages 1 and 2: repeated';
    else if (first) where = 'page 1';
    else if (second) where = 'page 2';
    else if (task.id === NEW_TASK.id)
      where = `added after page 1, at the ${order === 'oldest' ? 'end' : 'start'}`;
    else if (skipped.includes(task.id)) where = 'skipped';
    return [task.id, task.title, where];
  });

  const eventSentence =
    event === 'delete'
      ? 'task 9 is deleted'
      : event === 'insert'
        ? `task 15 "${NEW_TASK.title}" is added`
        : '';
  const between =
    loaded === 2
      ? event === 'delete'
        ? 'Task 9 was deleted'
        : event === 'insert'
          ? `Task 15 "${NEW_TASK.title}" was added`
          : 'Nothing changed'
      : event === 'none'
        ? 'Nothing will change'
        : `${pagingEventLabels[event]} when you load page 2`;

  let message: string;
  let tone: Tone = 'neutral';
  if (!page1)
    message = `Load page 1, then page 2. ${
      event === 'none'
        ? 'Nothing changes between the two loads.'
        : `Between the two loads, ${eventSentence}.`
    }`;
  else if (!page2)
    message = `Page 1 shows tasks ${natural(page1.shown.map((task) => task.id))}. ${
      event === 'none' ? 'Load page 2.' : `Load page 2: first, ${eventSentence}.`
    }`;
  else if (skipped.length) {
    message = `Task ${natural(skipped)} was skipped: deleting task 9 moved every later task up one place, and OFFSET ${PAGE_SIZE} still counted ${PAGE_SIZE} rows from the start.`;
    tone = 'warning';
  } else if (repeated.length) {
    message = `Task ${natural(repeated)} was repeated: task 15 went in at the start, every task moved down one place, and OFFSET ${PAGE_SIZE} still counted ${PAGE_SIZE} rows from the start.`;
    tone = 'warning';
  } else if (method === 'offset') {
    message =
      event === 'none'
        ? 'No task was skipped or repeated: nothing changed between the pages. OFFSET counts positions, so it is only right while the rows before that position stay put.'
        : event === 'delete'
          ? 'No task was skipped or repeated: task 9 comes after both pages in this newest-first list, so deleting it moved no row OFFSET counted.'
          : 'No task was skipped or repeated: task 15 has the highest ID, so it went in at the end of this oldest-first list, after every row OFFSET counted.';
  } else {
    const start = page1.shown.at(-1)?.id ?? 0;
    message = `No task was skipped or repeated: page 2 started after task ${start}, the last task page 1 showed (${afterCondition(order, start)})${
      event === 'none' ? '.' : ', so the change between the pages could not move where it starts.'
    }`;
    tone = 'success';
  }
  return { page1, page2, rows, skipped, repeated, between, message, tone };
}
